import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { Profile, Post, Friend, Comment, UserPreference, PostMedia, PostTag, ReactionType } from '../types/database';
import { useAuth } from '../hooks/useAuth';
import { UserPlus, UserMinus, UserCheck, Clock, X, UserX2, MoreVertical, Pencil, Trash2, Users } from 'lucide-react';
import { cn } from '../lib/utils';
import Comments from '../components/Comments';
import MediaGallery from '../components/MediaGallery';
import UserVisibilityControls from '../components/UserVisibilityControls';
import EditPost from '../components/EditPost';
import PostInteractions from '../components/PostInteractions';
import { useUrlPreviews } from '../hooks/useUrlPreviews';

interface PostWithCounts extends Post {
  likes_count?: number;
  comments_count?: number;
  user_has_liked?: boolean;
  profiles?: Profile;
  post_media?: PostMedia[];
  post_tags?: (PostTag & { tagged_user?: Profile })[];
}

export default function UserProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [posts, setPosts] = useState<PostWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [friendship, setFriendship] = useState<Friend | null>(null);
  const [userPreference, setUserPreference] = useState<UserPreference | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCommentsForPost, setShowCommentsForPost] = useState<string | null>(null);
  const [comments, setComments] = useState<Record<string, Comment[]>>({});
  const [loadingComments, setLoadingComments] = useState<Record<string, boolean>>({});
  const [canViewPosts, setCanViewPosts] = useState(false);
  const [fullscreenGallery, setFullscreenGallery] = useState<{ media: PostMedia[]; currentIndex: number } | null>(null);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [menuOpenPostId, setMenuOpenPostId] = useState<string | null>(null);
  const { urlMetadata, extractUrls, fetchUrlMetadata, renderContent } = useUrlPreviews();

  useEffect(() => {
    if (!id || !user || typeof id !== 'string' || id.trim() === '') {
      setError('No user ID provided');
      setLoading(false);
      return;
    }

    loadProfile();
  }, [id, user]);

  useEffect(() => {
    // Fetch metadata for URLs in posts
    posts.forEach(post => {
      if (post.description) {
        const urls = extractUrls(post.description);
        urls.forEach(url => {
          if (!urlMetadata[url]) {
            fetchUrlMetadata(url);
          }
        });
      }
    });
  }, [posts]);

  const loadProfile = async () => {
    if (!id || !user || typeof id !== 'string' || id.trim() === '') {
      setError('Invalid user ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Load profile data
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', id)
        .single();

      if (profileError) {
        console.error('Error loading profile:', profileError);
        setError('Failed to load profile');
        setLoading(false);
        return;
      }

      if (!profileData) {
        setError('User not found');
        setLoading(false);
        return;
      }

      setProfile(profileData);

      // Load user preferences
      const { data: preferenceData } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .eq('target_user_id', id)
        .single();

      setUserPreference(preferenceData);

      // Check if viewing own profile
      const isOwnProfile = id === user.id;
      
      if (isOwnProfile) {
        setCanViewPosts(true);
        loadUserPosts();
      } else {
        // Check friendship status
        const { data: friendshipData } = await supabase
          .from('friends')
          .select('*')
          .or(`and(sender_id.eq.${user.id},receiver_id.eq.${id}),and(sender_id.eq.${id},receiver_id.eq.${user.id})`)
          .order('created_at', { ascending: false })
          .limit(1);

        const currentFriendship = friendshipData?.[0] || null;
        setFriendship(currentFriendship);

        // Can view posts if friends or have a pending request to accept
        const canView = isOwnProfile || 
                       (currentFriendship && currentFriendship.status === 'accepted') || 
                       (currentFriendship && currentFriendship.status === 'pending' && currentFriendship.receiver_id === user.id);
        
        setCanViewPosts(canView);
        
        if (canView) {
          loadUserPosts();
        }
      }
    } catch (err: any) {
      console.error('Error loading profile:', err);
      setError('Failed to load profile');
      setProfile(null);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUserPosts = async () => {
    if (!id || !user) return;

    try {
      const { data: postsData, error: postsError } = await supabase
        .from('posts')
        .select(`
          *,
          profiles!posts_user_id_fkey (
            id,
            full_name,
            avatar_url
          ),
          post_tags (
            id,
            tagged_user_id,
            tagged_by_user_id,
            tagged_user:profiles!post_tags_tagged_user_id_fkey (
              id,
              full_name,
              avatar_url
            )
          ),
          post_likes (count),
          comments (count),
          post_media (
            id,
            url,
            type,
            position
          )
        `)
        .eq('user_id', id)
        .order('created_at', { ascending: false });

      if (postsError) throw postsError;

      const { data: userReactions } = await supabase
        .from('post_likes')
        .select('post_id, reaction_type')
        .eq('user_id', user.id);

      const userReactionsMap = new Map(userReactions?.map(r => [r.post_id, r.reaction_type]) || []);

      const enrichedPosts = (postsData || []).map(post => ({
        ...post,
        likes_count: post.post_likes?.[0]?.count || 0,
        comments_count: post.comments?.[0]?.count || 0,
        user_has_liked: userReactionsMap.has(post.id),
        user_reaction_type: userReactionsMap.get(post.id) || null,
        post_media: post.post_media?.sort((a, b) => a.position - b.position) || [],
      }));

      setPosts(enrichedPosts);
    } catch (err) {
      console.error('Error loading posts:', err);
    }
  };

  const handleFriendRequest = async () => {
    if (!user || !profile) return;

    try {
      setActionLoading(true);
      setError(null);

      const { error } = await supabase
        .from('friends')
        .insert({
          sender_id: user.id,
          receiver_id: profile.id,
          status: 'pending'
        });

      if (error) throw error;

      loadProfile();
    } catch (err: any) {
      console.error('Error sending friend request:', err);
      setError('Failed to send friend request. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAcceptFriendRequest = async () => {
    if (!friendship) return;

    try {
      setActionLoading(true);
      setError(null);

      const { error } = await supabase
        .from('friends')
        .update({ status: 'accepted' })
        .eq('id', friendship.id);

      if (error) throw error;

      loadProfile();
    } catch (err: any) {
      console.error('Error accepting friend request:', err);
      setError('Failed to accept friend request. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectFriendRequest = async () => {
    if (!friendship) return;

    try {
      setActionLoading(true);
      setError(null);

      const { error } = await supabase
        .from('friends')
        .update({ status: 'rejected' })
        .eq('id', friendship.id);

      if (error) throw error;

      loadProfile();
    } catch (err: any) {
      console.error('Error rejecting friend request:', err);
      setError('Failed to reject friend request. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnfriend = async () => {
    if (!friendship) return;

    try {
      setActionLoading(true);
      setError(null);

      const { error } = await supabase
        .from('friends')
        .delete()
        .eq('id', friendship.id);

      if (error) throw error;

      loadProfile();
    } catch (err: any) {
      console.error('Error unfriending:', err);
      setError('Failed to unfriend. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefollow = async () => {
    if (!user || !profile) return;

    try {
      setActionLoading(true);
      setError(null);

      const { error: deleteError } = await supabase
        .from('user_preferences')
        .delete()
        .eq('user_id', user.id)
        .eq('target_user_id', profile.id);

      if (deleteError) throw deleteError;

      await loadProfile();
      
      if (canViewPosts) {
        await loadUserPosts();
      }
    } catch (err: any) {
      console.error('Error re-following user:', err);
      setError('Failed to re-follow user. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnfollow = async () => {
    if (!user || !profile) return;

    try {
      setActionLoading(true);
      setError(null);

      const { error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          target_user_id: profile.id,
          is_following: false,
          snoozed_until: null
        })
        .select()
        .single();

      if (error) throw error;

      await loadProfile();
      
      if (canViewPosts) {
        await loadUserPosts();
      }
    } catch (err: any) {
      console.error('Error unfollowing user:', err);
      setError('Failed to unfollow user. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReaction = async (post: PostWithCounts, reactionType: ReactionType) => {
    if (!user) return;

    try {
      setError(null);

      if (post.user_reaction_type === reactionType) {
        const { error } = await supabase
          .from('post_likes')
          .delete()
          .eq('post_id', post.id)
          .eq('user_id', user.id);

        if (error) throw error;

        setPosts(posts.map(p =>
          p.id === post.id
            ? {
                ...p,
                likes_count: Math.max((p.likes_count || 0) - 1, 0),
                user_has_liked: false,
                user_reaction_type: null
              }
            : p
        ));
      } else if (post.user_reaction_type) {
        const { error } = await supabase
          .from('post_likes')
          .update({ reaction_type: reactionType })
          .eq('post_id', post.id)
          .eq('user_id', user.id);

        if (error) throw error;

        setPosts(posts.map(p =>
          p.id === post.id
            ? {
                ...p,
                user_reaction_type: reactionType
              }
            : p
        ));
      } else {
        const { error } = await supabase
          .from('post_likes')
          .insert({
            post_id: post.id,
            user_id: user.id,
            reaction_type: reactionType
          });

        if (error) throw error;

        setPosts(posts.map(p =>
          p.id === post.id
            ? {
                ...p,
                likes_count: (p.likes_count || 0) + 1,
                user_has_liked: true,
                user_reaction_type: reactionType
              }
            : p
        ));
      }
    } catch (error: any) {
      console.error('Error toggling reaction:', error);
      setError('Failed to update reaction. Please try again.');
    }
  };

  const fetchComments = async (postId: string) => {
    if (loadingComments[postId]) return;

    try {
      setLoadingComments(prev => ({ ...prev, [postId]: true }));

      const { data: commentsData, error: commentsError } = await supabase
        .from('comments')
        .select(`
          *,
          profiles (
            id,
            full_name,
            avatar_url
          )
        `)
        .eq('post_id', postId)
        .order('created_at', { ascending: true });

      if (commentsError) throw commentsError;

      setComments(prev => ({ ...prev, [postId]: commentsData || [] }));
    } catch (error: any) {
      console.error('Error fetching comments:', error);
      setError('Failed to load comments. Please try again.');
    } finally {
      setLoadingComments(prev => ({ ...prev, [postId]: false }));
    }
  };

  const toggleComments = async (postId: string) => {
    if (showCommentsForPost === postId) {
      setShowCommentsForPost(null);
    } else {
      setShowCommentsForPost(postId);
      await fetchComments(postId);
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!user) return;

    const confirmDelete = window.confirm('Are you sure you want to delete this post?');
    if (!confirmDelete) return;

    try {
      setError(null);
      const { error } = await supabase
        .from('posts')
        .delete()
        .eq('id', postId)
        .eq('user_id', user.id);

      if (error) throw error;

      setPosts(posts.filter(post => post.id !== postId));
      setMenuOpenPostId(null);
    } catch (error: any) {
      console.error('Error deleting post:', error);
      setError(error.message || 'Failed to delete post. Please try again.');
    }
  };

  const handleCommentAdded = async (postId: string, newComment: Comment & { profiles?: Profile }) => {
    setComments(prev => ({
      ...prev,
      [postId]: [...(prev[postId] || []), newComment]
    }));

    setPosts(prev => prev.map(post => 
      post.id === postId 
        ? { ...post, comments_count: (post.comments_count || 0) + 1 }
        : post
    ));
  };

  const getAvatarUrl = (profileData?: Profile | null) => {
    const targetProfile = profileData || profile;
    if (!targetProfile) return `https://api.dicebear.com/7.x/initials/svg?seed=guest`;

    if (targetProfile.avatar_url) {
      if (targetProfile.avatar_url.startsWith('http')) {
        return targetProfile.avatar_url;
      }
      const { data } = supabase.storage.from('avatars').getPublicUrl(targetProfile.avatar_url);
      return data.publicUrl;
    }

    return `https://api.dicebear.com/7.x/initials/svg?seed=${targetProfile.full_name}`;
  };

  const getFriendshipStatusMessage = () => {
    if (!friendship) {
      return "You need to be friends with this user to see their posts.";
    } else if (friendship.status === 'pending') {
      return friendship.sender_id === user?.id
        ? "You've sent a friend request to this user. You'll be able to see their posts once they accept."
        : "This user has sent you a friend request.";
    } else {
      return "You need to be friends with this user to see their posts.";
    }
  };

  const renderTaggedFriends = (post: PostWithCounts) => {
    if (!post.post_tags || post.post_tags.length === 0) return null;

    const taggedUsers = post.post_tags
      .map(tag => tag.tagged_user)
      .filter(Boolean) as Profile[];

    if (taggedUsers.length === 0) return null;

    const displayNames = taggedUsers.slice(0, 3);
    const remainingCount = taggedUsers.length - 3;

    return (
      <span className="text-sm text-gray-500 inline">
        {' is with '}
        {displayNames.map((user, index) => (
          <React.Fragment key={user.id}>
            <button
              onClick={() => navigate(`/user/${user.id}`)}
              className="text-blue-500 hover:text-blue-700 hover:underline"
            >
              {user.full_name}
            </button>
            {index < displayNames.length - 1 && (
              <span>{index === displayNames.length - 2 ? ' and ' : ', '}</span>
            )}
          </React.Fragment>
        ))}
        {remainingCount > 0 && (
          <span> and {remainingCount} other{remainingCount > 1 ? 's' : ''}</span>
        )}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <p className="text-xl font-semibold text-gray-900 mb-2">
            {error === 'User not found' ? '404 - User Not Found' : 'Error'}
          </p>
          <p className="text-gray-600 mb-6">
            {error === 'User not found' 
              ? 'This user does not exist.'
              : 'An error occurred while loading the profile.'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Return to Feed
          </button>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto py-8 px-4">
        {/* Profile Header */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-purple-500 h-32" />
          <div className="px-6 pb-6">
            <div className="flex flex-col items-center -mt-16">
              <img
                src={getAvatarUrl()}
                alt={profile.full_name}
                className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover bg-white"
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.src = `https://api.dicebear.com/7.x/initials/svg?seed=${profile.full_name}`;
                }}
              />
              <h1 className="mt-4 text-2xl font-bold text-gray-900">
                {profile.full_name}
              </h1>

              {/* Friend Request Button */}
              {user && profile && user.id !== profile.id && (
                <div className="mt-4 flex items-center space-x-2">
                  {!friendship ? (
                    <button
                      onClick={handleFriendRequest}
                      disabled={actionLoading}
                      className={cn(
                        "flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors",
                        "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
                        actionLoading && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      <UserPlus className="h-5 w-5" />
                      <span>Add Friend</span>
                    </button>
                  ) : friendship.status === 'pending' ? (
                    friendship.sender_id === user.id ? (
                      <button
                        onClick={handleUnfriend}
                        disabled={actionLoading}
                        className={cn(
                          "flex items-center space-x-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors",
                          "focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2",
                          actionLoading && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        <Clock className="h-5 w-5" />
                        <span>Cancel Request</span>
                      </button>
                    ) : (
                      <div className="flex space-x-2">
                        <button
                          onClick={handleAcceptFriendRequest}
                          disabled={actionLoading}
                          className={cn(
                            "flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors",
                            "focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2",
                            actionLoading && "opacity-50 cursor-not-allowed"
                          )}
                        >
                          <UserCheck className="h-5 w-5" />
                          <span>Accept</span>
                        </button>
                        <button
                          onClick={handleRejectFriendRequest}
                          disabled={actionLoading}
                          className={cn(
                            "flex items-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors",
                            "focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2",
                            actionLoading && "opacity-50 cursor-not-allowed"
                          )}
                        >
                          <X className="h-5 w-5" />
                          <span>Reject</span>
                        </button>
                      </div>
                    )
                  ) : friendship.status === 'accepted' ? (
                    <div className="flex space-x-2">
                      <button
                        onClick={handleUnfriend}
                        disabled={actionLoading}
                        className={cn(
                          "flex items-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors",
                          "focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2",
                          actionLoading && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        <UserMinus className="h-5 w-5" />
                        <span>Unfriend</span>
                      </button>
                      {userPreference?.is_following === false && (
                        <button
                          onClick={handleRefollow}
                          disabled={actionLoading}
                          className={cn(
                            "flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors",
                            "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
                            actionLoading && "opacity-50 cursor-not-allowed"
                          )}
                        >
                          <UserPlus className="h-5 w-5" />
                          <span>Re-follow</span>
                        </button>
                      )}
                      {!userPreference?.is_following && (
                        <button
                          onClick={handleUnfollow}
                          disabled={actionLoading}
                          className={cn(
                            "flex items-center space-x-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors",
                            "focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2",
                            actionLoading && "opacity-50 cursor-not-allowed"
                          )}
                        >
                          <UserX2 className="h-5 w-5" />
                          <span>Unfollow</span>
                        </button>
                      )}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* User's Posts */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Posts</h2>
          {!canViewPosts ? (
            <div className="bg-white rounded-lg shadow-md p-6 text-center text-gray-500">
              {getFriendshipStatusMessage()}
            </div>
          ) : posts.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-6 text-center text-gray-500">
              No posts yet
            </div>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => {
                const isMenuOpen = menuOpenPostId === post.id;
                const isOwnPost = user && post.user_id === user.id;
                const isEditing = editingPostId === post.id;

                if (isEditing) {
                  return (
                    <EditPost
                      key={`edit-${post.id}`}
                      postId={post.id}
                      initialDescription={post.description || ''}
                      initialMedia={post.post_media || []}
                      initialTags={post.post_tags?.map(tag => tag.tagged_user).filter(Boolean) as Profile[] || []}
                      onSave={() => {
                        setEditingPostId(null);
                        loadUserPosts();
                      }}
                      onCancel={() => setEditingPostId(null)}
                    />
                  );
                }

                return (
                  <div key={post.id} className="bg-white rounded-lg shadow-md p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <img
                          src={getAvatarUrl()}
                          alt={profile?.full_name}
                          className="h-10 w-10 rounded-full object-cover bg-gray-100"
                          onError={(e) => {
                            const img = e.target as HTMLImageElement;
                            img.src = `https://api.dicebear.com/7.x/initials/svg?seed=${profile?.full_name}`;
                          }}
                        />
                        <div>
                          <div className="flex items-baseline space-x-1">
                            <p className="font-medium text-gray-900">
                              <span>{profile?.full_name}</span>
                              <span className="inline">{renderTaggedFriends(post)}</span>
                            </p>
                          </div>
                          <p className="text-xs text-gray-500">
                            {new Date(post.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      {isOwnPost && (
                        <div className="relative">
                          <button
                            onClick={() => setMenuOpenPostId(isMenuOpen ? null : post.id)}
                            className="p-2 rounded-full hover:bg-gray-100 focus:outline-none"
                          >
                            <MoreVertical className="h-5 w-5 text-gray-500" />
                          </button>

                          {isMenuOpen && (
                            <div className="absolute right-0 mt-1 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                              <div className="py-1" role="menu">
                                <button
                                  onClick={() => {
                                    setEditingPostId(post.id);
                                    setMenuOpenPostId(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit Post
                                </button>
                                <button
                                  onClick={() => handleDeletePost(post.id)}
                                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Post
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {post.description && renderContent(post.description)}
                    
                    {post.post_media && post.post_media.length > 0 && (
                      <div className="mt-4">
                        <MediaGallery
                          media={post.post_media}
                          onMediaClick={(index) => {
                            setFullscreenGallery({
                              media: post.post_media || [],
                              currentIndex: index
                            });
                          }}
                        />
                      </div>
                    )}

                    <div className="mt-4">
                      <PostInteractions
                        postId={post.id}
                        likesCount={post.likes_count || 0}
                        commentsCount={post.comments_count || 0}
                        userReactionType={post.user_reaction_type}
                        onReaction={(reactionType) => handleReaction(post, reactionType)}
                        onComment={() => toggleComments(post.id)}
                      />
                    </div>

                    {showCommentsForPost === post.id && (
                      <div className="mt-4 border-t border-gray-100 pt-4">
                        {loadingComments[post.id] ? (
                          <div className="flex justify-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
                          </div>
                        ) : (
                          <Comments
                            comments={comments[post.id] || []}
                            onCommentAdded={(newComment) => handleCommentAdded(post.id, newComment)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {fullscreenGallery && (
        <MediaGallery
          media={fullscreenGallery.media}
          onMediaClick={(index) => {
            setFullscreenGallery({
              ...fullscreenGallery,
              currentIndex: index
            });
          }}
          fullscreen
          currentIndex={fullscreenGallery.currentIndex}
          onClose={() => setFullscreenGallery(null)}
          onPrevious={() => {
            if (fullscreenGallery.currentIndex > 0) {
              setFullscreenGallery({
                ...fullscreenGallery,
                currentIndex: fullscreenGallery.currentIndex - 1
              });
            }
          }}
          onNext={() => {
            if (fullscreenGallery.currentIndex < fullscreenGallery.media.length - 1) {
              setFullscreenGallery({
                ...fullscreenGallery,
                currentIndex: fullscreenGallery.currentIndex + 1
              });
            }
          }}
        />
      )}
    </div>
  );
}